import { Checkbox, Form, Option, useFormApi } from "informed";
import * as React from "react";
import { v4 } from "uuid";
import { Phase, WebPortalClient } from "webportalclient";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { Dropdown } from "../../forms/informedv2/Dropdown";
import { MultiSelect } from "../../forms/informedv2/MultiSelect";
import { TextBox } from "../../forms/informedv2/TextBox";
import { Required } from "../../forms/validation/Required";
import Modal, { ModalSize } from "../../misc/components/modal";
import { ScheduleVisit } from "./VisitSchedule";
import { Study } from "webportalclient";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";

export interface VisitConfigurationCoreProps {
    id: string;
    isCreate: boolean;
    possibleRequiredVisits: any[];
    onComplete: (session: ScheduleVisit) => void;
    onClone: () => void;
}

export interface VisitConfigurationProps {
    visitItem: ScheduleVisit;
    possibleRequiredVisits: any[];
    onComplete: (session: ScheduleVisit) => void;
    onClose: () => void;
    onClone: () => void;
}

interface VisitConfigurationForm {
    Label: string;
    Description?: string;
    Phase: string;
    Mandatory: boolean;
    Disabled: boolean;
    CanSpanDays: boolean;
    CommentRequired: boolean;
    ConfigurationRequired: boolean;
    RequiredVisits: any[];
    Cohort: string;
}

export const VisitConfiguration = ({ visitItem, possibleRequiredVisits, onComplete, onClose, onClone }: VisitConfigurationProps) => {
    const initialFormValues: VisitConfigurationForm = {
        Label: visitItem?.label,
        Description: visitItem?.description,
        Phase: visitItem?.phase,
        Mandatory: visitItem?.isMandatory ?? false,
        Disabled: visitItem?.disabled ?? false,
        CanSpanDays: visitItem?.canSpanDays ?? false,
        CommentRequired: visitItem?.commentRequired ?? false,
        ConfigurationRequired: visitItem?.confirmationRequired ?? false,
        RequiredVisits: visitItem?.requiredVisits,
        Cohort: visitItem?.cohort
    }

    return (
        <Modal show={true} header="Visit Configuration" size={ModalSize.Large} closeModalHandler={onClose} automationId="VisitConfigurationModal">
            <Form<VisitConfigurationForm> noValidate initialValues={initialFormValues}>
                <VisitConfigurationCore id={visitItem?.id ?? v4()} possibleRequiredVisits={possibleRequiredVisits} isCreate={!visitItem} onComplete={onComplete} onClone={onClone} />
            </Form>
        </Modal>
    );
}

export const GetReferenceOrStudyPhases = async (client: WebPortalClient, study: Study, phaseSetter: React.Dispatch<React.SetStateAction<Phase[]>>) => {
    const phasesResult = await client.getStudySchedulePhases(undefined, `studyId eq ${study.id}`);

    if (phasesResult.value.length === 0) {
        const referencePhases = await client.getPhases();
        phaseSetter(referencePhases.value);
    } else {
        phaseSetter(phasesResult.value.map(p => ({ id: p.phaseId, name: p.name })));
    }
}

export const VisitConfigurationCore = ({ id, isCreate, possibleRequiredVisits, onComplete, onClone }: VisitConfigurationCoreProps) => {
    const formApi = useFormApi<VisitConfigurationForm>();
    const [phases, setPhases] = React.useState<Phase[]>([]);
    const client = useWebPortalApiClient();
    const { study } = useCurrentStudyConfigurationContext();

    React.useEffect(() => {
        GetReferenceOrStudyPhases(client.client, study, setPhases);
    }, [client.client, study]);

    const onSubmit = async () => {
        formApi.validate();
        const state = formApi.getState();

        if (state.invalid)
            return;

        const visit: ScheduleVisit = {
            id: id,
            label: state.values.Label,
            isMandatory: state.values.Mandatory,
            disabled: state.values.Disabled,
            canSpanDays: state.values.CanSpanDays,
            commentRequired: state.values.CommentRequired,
            confirmationRequired: state.values.ConfigurationRequired,
            description: state.values.Description,
            phase: state.values.Phase,
            requiredVisits: state.values.RequiredVisits,
            sessions: [],
            cohort: state.values.Cohort
        }

        onComplete(visit);
    }

    return (
        <>
            <div className="modal-body">
                <div className="row">
                    <div className="form-group col-md-6 col-xs-12">
                        <label htmlFor="label">Label</label>
                        <TextBox id="Label" field="Label" validate={v => Required(v)} />
                    </div>
                    <div className="form-group col-md-6 col-xs-6">
                        <label htmlFor="Phase">Phase</label>
                        <Dropdown
                            id="Phase"
                            field="Phase"
                            validate={Required}
                        >
                            <Option
                                key={"OptionBlank"}
                                value={[]}
                            ></Option>
                            {phases.map(st => (
                                <Option
                                    key={`Option${st.id}`}
                                    value={st.id}
                                >
                                    {st.name}
                                </Option>
                            ))}
                        </Dropdown>
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-2 col-sm-2 col-xs-6">
                        <Checkbox id="Mandatory" field="Mandatory" />&nbsp;
                        <label htmlFor="Mandatory">Is Mandatory</label>
                    </div>
                    <div className="form-group col-md-2 col-sm-2 col-xs-6">
                        <Checkbox id="Disabled" field="Disabled" />&nbsp;
                        <label htmlFor="Disabled">Disabled</label>
                    </div>
                    <div className="form-group col-md-2 col-sm-2 col-xs-6">
                        <Checkbox id="CanSpanDays" field="CanSpanDays" />&nbsp;
                        <label htmlFor="CanSpanDays">Can Span Days</label>

                    </div>
                    <div className="form-group col-md-3 col-sm-3 col-xs-6">
                        <Checkbox id="CommentRequired" field="CommentRequired" />&nbsp;
                        <label htmlFor="CommentRequired">Comment Required</label>
                    </div>
                    <div className="form-group col-md-3 col-sm-3 col-xs-6">
                        <Checkbox id="ConfigurationRequired" field="ConfigurationRequired" />&nbsp;
                        <label htmlFor="ConfigurationRequired">Configuration Required</label>
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-12 col-xs-12">
                        <label htmlFor="Description">Description</label>
                        <TextBox id="Description" field="Description" />
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-12 col-xs-12">
                        <label htmlFor="RequiredSessions">Required Visits</label>
                        <MultiSelect
                            id="RequiredVisits"
                            field="RequiredVisits"
                            options={possibleRequiredVisits}
                            labelKey="Label"
                            valueKey="Id"
                            multi={true}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="form-group col-md-12 col-xs-12">
                        <label htmlFor="Cohort">Cohort</label>
                        <TextBox id="Cohort" field="Cohort" />
                    </div>
                </div>
            </div>
            <div className="modal-footer">
                {!isCreate ? <button className="btn btn-primary btn-outline" onClick={onClone}>Clone</button> : null}
                <button className="btn btn-primary" onClick={onSubmit} data-automationid="VisitConfiguration_Save">Save</button>
            </div>
        </>
    );
}
